import type { CSSProperties } from 'react';

import type {
  BaseStyleProps,
  BoxStyleProps,
  CSSBackgroundProps,
  CSSBorderProps,
  CSSBorderValue,
  CSSColorValue,
  CSSLayoutProps,
  CSSPositioningProps,
  CSSSizingProps,
  CSSSpacingProps,
  CSSSpacingValue,
  FlexStyleProps,
} from '../types';

type StyleHandler = (value: any) => string;

type StyleHandlers<T> = {
  [key in keyof Required<T>]: [string, StyleHandler];
};

const SPACING_VALUE_REGEX = /^space-\d+x$/;
const isSpacingValue = (value: any): value is CSSSpacingValue => SPACING_VALUE_REGEX.test(value);

const passThrough = (value: any): string => value;
const spacingHandler = (value: CSSSpacingValue): string => `var(--banana-${value})`;
const colorHandler = (value: CSSColorValue): string => `var(--banana-color-${value})`;
const borderSizeHandler = (value: CSSBorderValue): string => `var(--banana-${value})`;
const boxDisplayHandler = (value: BoxStyleProps['display'] = 'block'): string => passThrough(value);
const flexDisplayHandler = (value: FlexStyleProps['display'] = 'flex'): string => passThrough(value);

const positioningHandler = (value: CSSColorValue | CSSPositioningProps[keyof CSSPositioningProps]): string => {
  if (isSpacingValue(value)) {
    return spacingHandler(value);
  }

  return passThrough(value);
};

const layoutHandlers: StyleHandlers<CSSLayoutProps> = {
  alignSelf: ['alignSelf', passThrough],
  flex: ['flex', passThrough],
  flexBasis: ['flexBasis', passThrough],
  flexGrow: ['flexGrow', passThrough],
  flexShrink: ['flexShrink', passThrough],
  justifySelf: ['justifySelf', passThrough],
  order: ['order', passThrough],
  overflow: ['overflow', passThrough],
};

const spacingHandlers: StyleHandlers<CSSSpacingProps> = {
  margin: ['margin', spacingHandler],
  marginBlock: ['marginBlock', spacingHandler],
  marginBlockEnd: ['marginBlockEnd', spacingHandler],
  marginBlockStart: ['marginBlockStart', spacingHandler],
  marginInline: ['marginInline', spacingHandler],
  marginInlineEnd: ['marginInlineEnd', spacingHandler],
  marginInlineStart: ['marginInlineStart', spacingHandler],
  padding: ['padding', spacingHandler],
  paddingBlock: ['paddingBlock', spacingHandler],
  paddingBlockEnd: ['paddingBlockEnd', spacingHandler],
  paddingBlockStart: ['paddingBlockStart', spacingHandler],
  paddingInline: ['paddingInline', spacingHandler],
  paddingInlineEnd: ['paddingInlineEnd', spacingHandler],
  paddingInlineStart: ['paddingInlineStart', spacingHandler],
};

const sizingHandlers: StyleHandlers<CSSSizingProps> = {
  height: ['height', passThrough],
  maxHeight: ['maxHeight', passThrough],
  maxWidth: ['maxWidth', passThrough],
  minHeight: ['minHeight', passThrough],
  minWidth: ['minWidth', passThrough],
  width: ['width', passThrough],
};

const backgroundHandlers: StyleHandlers<CSSBackgroundProps> = {
  backgroundColor: ['backgroundColor', colorHandler],
};

const borderHandlers: StyleHandlers<CSSBorderProps> = {
  borderEndEndRadius: ['borderEndEndRadius', borderSizeHandler],
  borderEndStartRadius: ['borderEndStartRadius', borderSizeHandler],
  borderRadius: ['borderRadius', borderSizeHandler],
  borderStartEndRadius: ['borderStartEndRadius', borderSizeHandler],
  borderStartStartRadius: ['borderStartStartRadius', borderSizeHandler],
};

const positioningHandlers: StyleHandlers<CSSPositioningProps> = {
  bottom: ['bottom', positioningHandler],
  insetInlineEnd: ['insetInlineEnd', positioningHandler],
  insetInlineStart: ['insetInlineStart', positioningHandler],
  position: ['position', passThrough],
  top: ['top', positioningHandler],
};

export const baseStyleHandlers: StyleHandlers<BaseStyleProps> = {
  ...layoutHandlers,
  ...spacingHandlers,
  ...sizingHandlers,
  ...backgroundHandlers,
  ...borderHandlers,
  ...positioningHandlers,
};

export const boxStyleHandlers: StyleHandlers<BoxStyleProps> = {
  ...baseStyleHandlers,
  display: ['display', boxDisplayHandler],
};

export const flexStyleHandlers: StyleHandlers<FlexStyleProps> = {
  ...baseStyleHandlers,
  alignContent: ['alignContent', passThrough],
  alignItems: ['alignItems', passThrough],
  columnGap: ['columnGap', spacingHandler],
  direction: ['flexDirection', passThrough],
  display: ['display', flexDisplayHandler],
  gap: ['gap', spacingHandler],
  justifyContent: ['justifyContent', passThrough],
  rowGap: ['rowGap', spacingHandler],
  wrap: ['flexWrap', passThrough],
};

/**
 * Applies style props to a CSSProperties object based on the provided handlers.
 */
export default function useStyleProps<T extends BaseStyleProps>(
  props: T,
  handlers: StyleHandlers<T> = boxStyleHandlers as StyleHandlers<T>,
): CSSProperties {
  const style: CSSProperties = {};

  for (const key in props) {
    const handler = handlers[key];
    if (!handler || props[key] == null) {
      continue;
    }

    const [name, convert] = handler;

    const value = convert(props[key]);

    // @ts-expect-error - we know that name is a valid CSS property
    style[name] = value;
  }

  // @ts-expect-error - we know that `style` might coming from the props and it's a valid CSSProperties
  const propStyle = props.style;

  return {
    ...propStyle,
    ...style,
  };
}
